import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useCreatePaymentIntent } from '@/api/queries/payments';
import { dollarsInputToCents } from '@/utils/money';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/store/ui';
import { isAPIError } from '@/api/error';

export function WalletTopupPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [amount, setAmount] = useState('100');
  const intent = useCreatePaymentIntent();
  const pushToast = useUIStore((s) => s.pushToast);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cents = dollarsInputToCents(amount);
    if (cents === null || cents < 100) {
      pushToast({ body: t('errors.validation'), variant: 'error' });
      return;
    }
    try {
      const r = await intent.mutateAsync({ purpose: 'wallet_topup', amount_cents: cents });
      if (r.payment_page_url || r.widget_config) {
        navigate(paths.payIntent(r.intent_id));
        return;
      }
      if (r.checkout_url) {
        window.open(r.checkout_url, '_blank', 'noopener');
      }
    } catch (err) {
      const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
      pushToast({ body: t(key, { defaultValue: t('errors.generic') }), variant: 'error' });
    }
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('wallet.topup.title')} />
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input
            label={t('wallet.topup.amount_label')}
            inputMode="decimal"
            placeholder={t('wallet.topup.amount_placeholder')}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
          />
          <Card variant="light" padding="sm">
            <p style={{ margin: 0, color: 'var(--color-text-on-light-muted)' }}>
              {t('wallet.topup.open_checkout')}
            </p>
          </Card>
          <Button type="submit" block size="lg" variant="inverted" loading={intent.isPending}>
            {t('wallet.topup.submit')}
          </Button>
        </form>
      </PageContainer>
    </AppShell>
  );
}
